import { Head, Link, router, useForm } from "@inertiajs/react";
import { CheckCircle2, AlertCircle, CreditCard, Clock, Rocket } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Notice } from "@/components/ui/notice";
import StatusBadge from "@/components/shared/StatusBadge";
import { useConfirm } from "@/components/shared/ConfirmProvider";
import FormField from "@/components/forms/FormField";
import SelectField from "@/components/forms/SelectField";
import { TIMEZONES, timezoneLabel } from "@/lib/timezones";
import { formatDateRange } from "@/lib/format";
import NotificationPlanForm from "@/components/schools/NotificationPlanForm";
import SquareConnectAgreement from "@/components/schools/SquareConnectAgreement";

// What each campaign state means, in a sentence, for the campaign card.
const STATE_EXPLANATIONS = {
  draft: "Getting ready. It goes live by itself on its start date.",
  early_donations: "Donations are open. Reading starts on its start date, when it goes live by itself.",
  active: "Live. Families can donate, and teachers and families log reading.",
  grace: "Wrapping up. Reading has ended; donations stay open until the campaign finishes.",
  locked: "Finished. Donations and minute logging are closed and the results are final.",
};

export default function Settings({
  school, square_connection, campaign, update_path, connect_square_path, disconnect_square_path, membership,
  notifications, update_notifications_path, provinces, timezones,
}) {
  return (
    <SchoolLayout school={school} membership={membership} title="School settings">
      <Head title="Settings" />

      <div className="space-y-8">
        <SchoolDetailsCard school={school} provinces={provinces} timezones={timezones} updatePath={update_path} />
        <SquareCard connection={square_connection} connectPath={connect_square_path} disconnectPath={disconnect_square_path} campaign={campaign} />

        <Card>
          <CardHeader>
            <CardTitle>Notifications</CardTitle>
            <CardDescription>
              Which automatic emails PagePledge sends to your school admins, teachers and families.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <NotificationPlanForm plan={notifications} path={update_notifications_path} />
          </CardContent>
        </Card>

        {campaign && <CampaignCard campaign={campaign} updatePath={update_path} />}
      </div>
    </SchoolLayout>
  );
}

function SchoolDetailsCard({ school, provinces, timezones, updatePath }) {
  const { data, setData, patch, processing, errors } = useForm({
    name: school.name || "",
    address: school.address || "",
    city: school.city || "",
    province: school.province || "",
    postal_code: school.postal_code || "",
    timezone: school.timezone || "America/Toronto",
  });
  const timezoneOptions = (timezones || []).map((value) => ({ value, label: TIMEZONES.find((tz) => tz.value === value)?.label || timezoneLabel(value) }));
  if (data.timezone && !timezoneOptions.some((tz) => tz.value === data.timezone)) {
    timezoneOptions.unshift({ value: data.timezone, label: timezoneLabel(data.timezone) });
  }

  const submit = (e) => {
    e.preventDefault();
    patch(updatePath, { preserveScroll: true });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>School details</CardTitle>
        <CardDescription>Your school's name is on every donation page, receipt and sheet that goes home. The timezone decides when each campaign day starts and ends.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-4 lg:grid-cols-2">
            <FormField name="name" label="School name" required value={data.name} onChange={(e) => setData("name", e.target.value)} error={errors.name} />
            <FormField name="address" label="Address" value={data.address} onChange={(e) => setData("address", e.target.value)} error={errors.address} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FormField name="city" label="City" value={data.city} onChange={(e) => setData("city", e.target.value)} error={errors.city} />
            <SelectField
              name="province"
              label="Province or territory"
              value={data.province}
              onChange={(e) => setData("province", e.target.value)}
              placeholder="Choose…"
              options={provinces || []}
              error={errors.province}
            />
            <FormField name="postal_code" label="Postal code" value={data.postal_code} onChange={(e) => setData("postal_code", e.target.value)} error={errors.postal_code} />
            <SelectField
              name="timezone"
              label="Timezone"
              value={data.timezone}
              onChange={(e) => setData("timezone", e.target.value)}
              options={timezoneOptions}
              error={errors.timezone}
            />
          </div>
          <div className="border-t border-border pt-5">
            <Button type="submit" disabled={processing}>{processing ? "Saving…" : "Save changes"}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function SquareCard({ connection, connectPath, disconnectPath, campaign }) {
  const confirm = useConfirm();
  const live = campaign && ["active", "grace"].includes(campaign.state);

  const disconnect = async () => {
    const ok = await confirm({
      title: "Disconnect Square?",
      body: live
        ? `${campaign.name} is taking donations. Until you connect Square again, nobody can donate.`
        : "Nobody can donate until you connect Square again. Donations already made aren't affected.",
      confirmLabel: "Disconnect Square",
      destructive: true,
    });
    if (ok) router.delete(disconnectPath, { preserveScroll: true });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary" />
          Square
        </CardTitle>
        <CardDescription>Donations go straight to your school's Square account. PagePledge never holds the money.</CardDescription>
      </CardHeader>
      <CardContent>
        {connection ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-success" />
              <div>
                <p className="font-medium text-foreground">Connected</p>
                {connection.location_name && <p className="mt-0.5 text-sm text-muted-foreground">Donations go to {connection.location_name}.</p>}
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={disconnect} className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive">
              Disconnect
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 text-warning-foreground" />
              <div>
                <p className="font-medium text-foreground">Not connected</p>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {connectPath
                    ? "Connect Square to take donations. The account needs its bank account verified before card payments go through."
                    : "You can connect Square once PagePledge approves your first campaign."}
                </p>
              </div>
            </div>
            {/* No connect path until PagePledge approves a campaign (§2a). */}
            {connectPath && <SquareConnectAgreement href={connectPath} size="default" />}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CampaignCard({ campaign, updatePath }) {
  const confirm = useConfirm();

  const change = async (action) => {
    const ok = await confirm({
      title: `${action.label}?`,
      body: action.description,
      confirmLabel: action.label,
      destructive: action.state === "locked",
    });
    if (ok) router.patch(updatePath, { campaign_state: action.state, campaign_id: campaign.id }, { preserveScroll: true });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>{campaign.name}</CardTitle>
          <StatusBadge status={campaign.display_state || campaign.state} showActive />
        </div>
        <CardDescription>{formatDateRange(campaign.start_at, campaign.end_at)}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-foreground">{STATE_EXPLANATIONS[campaign.display_state || campaign.state]}</p>

        {campaign.schedule && (
          <div className="rounded-lg border border-border bg-background p-4 text-sm">
            <p className="mb-2 flex items-center gap-1.5 font-medium text-foreground">
              <Clock className="h-4 w-4 text-primary" />
              Schedule · {timezoneLabel(campaign.schedule.timezone)} time
            </p>
            <dl className="space-y-1.5 text-muted-foreground">
              <ScheduleRow label="It's now" value={campaign.schedule.current_time} />
              <ScheduleRow label="Reading ends" value={campaign.schedule.end_at} />
              <ScheduleRow label="Minute logging closes" value={campaign.schedule.minutes_close_at} />
              <ScheduleRow label="Donations close" value={campaign.schedule.grace_ends_at} />
            </dl>
            <p className="mt-2 text-xs text-muted-foreground">
              "It's now" should match the clock at your school. If it doesn't, change the timezone above.
            </p>
          </div>
        )}

        {campaign.launch_path && (
          <Notice
            icon={Rocket}
            action={
              <Button size="sm" variant="outline" asChild>
                <Link href={campaign.launch_path}>Launch checklist</Link>
              </Button>
            }
          >
            There's nothing to press to launch. The setup guide shows anything still needed before the start date.
          </Notice>
        )}

        {campaign.state_actions?.length > 0 && (
          <div className="space-y-3 border-t border-border pt-5">
            <p className="text-sm text-muted-foreground">
              Campaigns move on by themselves on their dates. If you need to, you can move this one on early.
            </p>
            <div className="flex flex-wrap gap-2">
              {campaign.state_actions.map((action) => (
                <Button
                  key={action.state}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => change(action)}
                  className={action.state === "locked" ? "border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive" : ""}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ScheduleRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <dt>{label}</dt>
      <dd className="text-right text-foreground font-tabular">{value}</dd>
    </div>
  );
}
